import { ref, computed } from '@vue/composition-api';
import { getSharedAssistantSession } from './sharedAssistantSession.js';

/**
 * Adapter: keeps the exact interface the legacy prod GovavaAIGift.vue
 * consumes (Tavus + Daily era), but the underlying runtime is OpenAI
 * Realtime + Anam wired through useAssistantSession.
 */
export function useTavusConversation() {
  const session = getSharedAssistantSession();

  // prod component state carried locally where no session equivalent exists
  const isSoundReduced = ref(false);
  const showTranscript = ref(false);
  const liveMessages = ref([]);
  const currentSpeaker = ref(null);
  const currentSpeech = ref('');
  const conversationData = ref(null);
  const conversationUrl = ref(null);
  const conversationTranscript = ref([]);

  // Set by the component the instant the user clicks, and cleared once the
  // session reports a real state. Without it there is a window between
  // "user clicked" and "startSession() set isStarting" where nothing is
  // true yet, and the status fell through to 'disconnected' — which is the
  // "Govava Avatar Disconnected" label flashing on every start.
  const startPending = ref(false);

  const connectionStatus = computed(() => {
    if (session.error.value) return 'error';
    if (session.isConnected.value) return 'connected';
    if (session.isStarting.value) return 'connecting';
    if (startPending.value) return 'connecting';
    return 'disconnected';
  });

  const connectionStatusText = computed(() => {
    const s = connectionStatus.value;
    if (s === 'connected') return 'Connected';
    if (s === 'connecting') return 'Connecting...';
    if (s === 'error') return session.error.value || 'Error';
    return 'Disconnected';
  });

  async function initializeConversation() {
    try {
      session.setAvatarVideoElementId('anam-avatar-video');
      await session.startSession();
    } catch (e) {
      console.warn('initializeConversation failed:', e);
    } finally {
      // Hand control back to the session's own refs. By now it is either
      // connected, errored, or genuinely failed — all of which the computed
      // reports correctly on its own.
      startPending.value = false;
    }
  }

  function setupIframe() { /* Anam uses its own <video>; nothing to do here */ }

  function toggleMute() { session.toggleMute(); }

  function reduceSound() { isSoundReduced.value = !isSoundReduced.value; }

  async function disconnectConversation() {
    // Deliberately does NOT clear startPending: startConversation() calls
    // this to clean up the previous session *while starting a new one*,
    // and clearing here would drop back to 'disconnected' mid-start —
    // exactly the flash this flag exists to prevent. initializeConversation's
    // finally block owns clearing it.
    await session.leaveKeepingGifts();
  }

  function displayTranscript() { showTranscript.value = !showTranscript.value; }

  function cleanup() { session.reset(); }

  function clearError() { session.clearError(); }

  /**
   * The component calls updateStatus('connecting') the moment the user
   * clicks, before any async work. This used to be a no-op, so that intent
   * was dropped and the UI showed 'disconnected' until the socket opened.
   * Only 'connecting' is honoured: every other state is owned by the
   * session's own refs.
   */
  function updateStatus(status) {
    startPending.value = status === 'connecting';
  }

  function updateVideoContainers() { /* no-op: Anam binds to a fixed <video id> */ }

  return {
    // state
    conversationData,
    conversationUrl,
    isConnected: session.isConnected,
    connectionStatus,
    connectionStatusText,
    conversationTranscript,
    showTranscript,
    liveMessages,
    currentSpeaker,
    currentSpeech,
    isMuted: session.isMuted,
    isSoundReduced,
    // actions
    initializeConversation,
    setupIframe,
    toggleMute,
    reduceSound,
    disconnectConversation,
    displayTranscript,
    cleanup,
    clearError,
    updateStatus,
    updateVideoContainers,
  };
}
